import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { LinearProgress } from '@material-ui/core';

import {
  usePayrollRunsListQuery,
  useRecreateDonationForTheNextPayrollRunMutation,
} from 'generated/graphql';
import { selectUser } from 'entities/user/selectors';
import { PayrollHeader } from 'containers/PayrollGenericPage/PayrollHeader';
import { type State } from 'reducers';

import { RetryModal } from '../PayrollGenericPage/RetryModal';

import { PayrollSchedule } from './PayrollSchedule/index';
import {
  DonationsFetchStatus,
  fetchDonationsForPayrollAction,
  selectDonationForReschedule,
} from './types';
import {
  selectDonationsForPayrollFetchStatus,
  selectSelectedDonationForReschedule,
} from './selectors';

export const PayrollApiPage = (): JSX.Element => {
  const user = useSelector(selectUser);
  const dispatch = useDispatch();
  const [selectedPayrollRun, setSelectedPayrollRun] = useState<string | null>(null);
  const selectedDonation = useSelector(selectSelectedDonationForReschedule);
  const donationsFetchStatus = useSelector((state: State) =>
    selectDonationsForPayrollFetchStatus(state, selectedPayrollRun)
  );
  const donationsLoading = donationsFetchStatus === DonationsFetchStatus.LOADING;
  const [
    recreateDonationForTheNextPayrollRunMutation,
    { loading: isReschedulingDonation, data: rescheduleDonationSuccess },
  ] = useRecreateDonationForTheNextPayrollRunMutation({
    onCompleted: (res) => {
      if (selectedPayrollRun && res.recreateDonationForTheNextPayrollRun) {
        dispatch(fetchDonationsForPayrollAction(selectedPayrollRun));
      }
    },
    onError: () => {
      // eslint-disable-next-line no-alert
      alert('Failed to reschedule donation');
    },
  });

  const { data, loading, error } = usePayrollRunsListQuery({});

  if (loading) {
    return <LinearProgress />;
  }

  if (error) {
    return <div>There was an error loading this page. Please try again.</div>;
  }

  const payrollRuns = data?.payrollRuns ?? [];

  const onConfirm = () => {
    if (!selectedDonation) {
      return;
    }
    recreateDonationForTheNextPayrollRunMutation({
      variables: { recreateDonationForTheNextPayrollRunId: selectedDonation },
    });
  };

  const setSelectedDonation = (donationId: string | null): void => {
    dispatch(selectDonationForReschedule(donationId));
  };

  return user ? (
    <>
      <RetryModal
        isOpen={Boolean(selectedDonation)}
        onClose={() => setSelectedDonation(null)}
        onConfirm={onConfirm}
        isLoading={isReschedulingDonation || donationsLoading}
      />
      <PayrollHeader />
      {payrollRuns.map((payrollRun) => (
        <PayrollSchedule
          key={payrollRun.id}
          payrollRun={payrollRun}
          setSelectedDonation={setSelectedDonation}
          selectedPayrollRun={selectedPayrollRun}
          setSelectedPayrollRun={setSelectedPayrollRun}
          rescheduleDonationSuccess={Boolean(rescheduleDonationSuccess)}
        />
      ))}
    </>
  ) : (
    <></>
  );
};
